import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import moment from "moment";
import _ from "lodash";
import BarChart from "./chartWidget/BarChart";
import LineChart from "./chartWidget/LineChart";
import PieChart from "./chartWidget/PieChart";
import SimpleLoadingIndicator from "./simpleLoadingIndicator";

const numFormatter = Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 2,
});
const TOP_ITEMS_LIMIT = 15;
const GI_ORDER_TYPE_SORT = ["NEW", "RENEW", "ENDOSE", "CREDIT"];

const formatCompactCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "0";
  return `${numFormatter.format(Number(value))}`;
};

const SalesPerformanceKpiCard = ({ label, value, bg, color = "white" }) => (
  <Paper
    radius="md"
    p="lg"
    shadow="md"
    sx={{
      backgroundColor: bg,
      minHeight: 90,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Text size="xs" weight={700} color={color} opacity={0.9} transform="uppercase">
      {label}
    </Text>
    <Text size="xl" weight={800} color={color} mt={4}>
      {value}
    </Text>
  </Paper>
);

const ChartMetricToggle = ({ value, onChange, options }) => (
  <SegmentedControl size="xs" value={value} onChange={onChange} data={options} />
);

const truncateLabel = (value) =>
  value?.length > 20 ? `${value.substring(0, 20)}...` : value;

const filterMonthlyTrend = (trend = []) => {
  const currentMonth = moment().format("YYYY-MM");

  return trend.filter((item) => {
    if (!item?.month || item.month > currentMonth) return false;
    return (item.grossPremium ?? 0) > 0 || (item.brokerage ?? 0) > 0;
  });
};

const formatMonthLabel = (month) => moment(month, "YYYY-MM").format("MMM");

const getGiOrderTypeLabel = (code, t) => {
  const defaults = {
    NEW: "New",
    RENEW: "Renew",
    ENDOSE: "Endorsement",
    CREDIT: "Credit Note",
  };
  return t(`dashboard.salesPerformance.giOrderType.${code}`, defaults[code] || code);
};

const DashboardSalesPerformanceTab = ({
  orderSummary = [],
  orderProductStats = {},
  giOrderTypeStats = [],
  salesPerformance = {},
  selectedStatuses = [],
  loading = false,
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [donutMetric, setDonutMetric] = useState("count");
  const [insuranceClassMetric, setInsuranceClassMetric] = useState("count");
  const [insurerMetric, setInsurerMetric] = useState("premium");

  const metricToggleOptions = useMemo(
    () => [
      {
        label: t("dashboard.salesPerformance.metricCount", "Policy Count"),
        value: "count",
      },
      {
        label: t("dashboard.salesPerformance.metricPremium", "Gross Premium"),
        value: "premium",
      },
    ],
    [t]
  );

  const kpiRows = useMemo(
    () => orderSummary.filter((row) => selectedStatuses.includes(row._id)),
    [orderSummary, selectedStatuses]
  );

  const kpis = useMemo(() => {
    const totalPolicies = _.sumBy(kpiRows, "count");
    const totalGrossPremium = _.sumBy(kpiRows, "yearPremium");
    const totalBrokerage = _.sumBy(kpiRows, "yearCommission");
    const totalTrCommission = _.sumBy(kpiRows, "yearConsultantCommission");
    const totalProfit = totalBrokerage - totalTrCommission;

    return {
      totalPolicies,
      totalGrossPremium,
      totalBrokerage,
      totalProfit,
      totalTrCommission,
    };
  }, [kpiRows]);

  const monthlyLineData = useMemo(() => {
    const trend = filterMonthlyTrend(salesPerformance?.monthlyTrend);
    return [
      {
        id: t("dashboard.salesPerformance.grossPremium", "Gross Premium"),
        color: "#74c0fc",
        data: trend.map((item) => ({
          x: item.month,
          y: item.grossPremium ?? 0,
        })),
      },
      {
        id: t("dashboard.salesPerformance.brokerage", "Brokerage"),
        color: "#1864ab",
        data: trend.map((item) => ({
          x: item.month,
          y: item.brokerage ?? 0,
        })),
      },
    ];
  }, [salesPerformance?.monthlyTrend, t]);

  const policyTypePieData = useMemo(() => {
    const valueKey = donutMetric === "premium" ? "totalYearPremium" : "count";
    return [...giOrderTypeStats]
      .sort(
        (a, b) =>
          GI_ORDER_TYPE_SORT.indexOf(a.code) - GI_ORDER_TYPE_SORT.indexOf(b.code)
      )
      .map((item) => ({
        id: getGiOrderTypeLabel(item.code, t),
        value: item[valueKey] ?? 0,
      }))
      .filter((item) => item.value > 0);
  }, [giOrderTypeStats, donutMetric, t]);

  const bizLinesPieData = useMemo(() => {
    const valueKey = donutMetric === "premium" ? "totalYearPremium" : "count";
    return (
      orderProductStats?.productCategory
        ?.map((item) => ({
          id: item.name || item.code,
          value: item[valueKey] ?? 0,
        }))
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value) ?? []
    );
  }, [orderProductStats?.productCategory, donutMetric]);

  const insuranceClassData = useMemo(() => {
    const valueKey =
      insuranceClassMetric === "premium" ? "totalYearPremium" : "count";
    return (
      orderProductStats?.productType
        ?.map((item) => ({
          id: item.name || item.code,
          value: item[valueKey] ?? 0,
        }))
        .filter((item) => item.value > 0)
        .sort((a, b) => a.value - b.value)
        .slice(-TOP_ITEMS_LIMIT) ?? []
    );
  }, [orderProductStats?.productType, insuranceClassMetric]);

  const insurerData = useMemo(() => {
    const valueKey = insurerMetric === "premium" ? "totalYearPremium" : "count";
    return (
      orderProductStats?.productProvider
        ?.map((item) => ({
          id: item.shortName || item.name || item.code,
          value: item[valueKey] ?? 0,
        }))
        .filter((item) => item.value > 0)
        .sort((a, b) => a.value - b.value)
        .slice(-TOP_ITEMS_LIMIT) ?? []
    );
  }, [orderProductStats?.productProvider, insurerMetric]);

  const topSalesChannels = salesPerformance?.topSalesChannels || [];

  const blueShades = [
    theme.colors.deepBlue?.[8] || "#1864ab",
    theme.colors.deepBlue?.[6] || "#1971c2",
    theme.colors.cyan?.[6] || "#15aabf",
    theme.colors.cyan?.[4] || "#3bc9db",
    theme.colors.cyan?.[2] || "#99e9f2",
  ];

  const insuranceClassTitle =
    insuranceClassMetric === "premium"
      ? t(
          "dashboard.salesPerformance.insuranceClassByPremium",
          "Insurance Class Distribution (Total Gross Premium)"
        )
      : t(
          "dashboard.salesPerformance.insuranceClassByPolicies",
          "Insurance Class Distribution (Total Policies)"
        );

  const insurerTitle =
    insurerMetric === "premium"
      ? t(
          "dashboard.salesPerformance.insurerByPremium",
          "Insurer Distribution (Total Gross Premium)"
        )
      : t(
          "dashboard.salesPerformance.insurerByPolicies",
          "Insurer Distribution (Total Policies)"
        );

  return (
    <Box pos="relative" mt="50px">
      <SimpleLoadingIndicator visible={loading} />
      <Grid gutter="md">
        <Grid.Col span={12} md={3}>
          <Stack spacing="sm">
            <SalesPerformanceKpiCard
              label={t("dashboard.salesPerformance.totalPolicies", "Total Policies")}
              value={kpis.totalPolicies}
              bg={blueShades[0]}
            />
            <SalesPerformanceKpiCard
              label={t(
                "dashboard.salesPerformance.totalGrossPremium",
                "Total Gross Premium"
              )}
              value={formatCompactCurrency(kpis.totalGrossPremium)}
              bg={blueShades[1]}
            />
            <SalesPerformanceKpiCard
              label={t("dashboard.salesPerformance.totalBrokerage", "Total Brokerage")}
              value={formatCompactCurrency(kpis.totalBrokerage)}
              bg={blueShades[2]}
            />
            <SalesPerformanceKpiCard
              label={t("dashboard.salesPerformance.totalProfit", "Total Profit")}
              value={formatCompactCurrency(kpis.totalProfit)}
              bg={theme.colors.pink?.[6] || "#e64980"}
            />
            <SalesPerformanceKpiCard
              label={t("dashboard.salesPerformance.trCommission", "TR Commission")}
              value={formatCompactCurrency(kpis.totalTrCommission)}
              bg={blueShades[0]}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={12} md={9}>
          <Stack spacing="md">
            <LineChart
              title={t(
                "dashboard.salesPerformance.monthlyTrend",
                "Monthly Gross Premium & Brokerage Trend"
              )}
              height={380}
              data={monthlyLineData}
              useSampleData={false}
              chartProps={{
                yScale: { type: "linear", stacked: false },
                axisLeft: {
                  format: (value) => formatCompactCurrency(value),
                },
                axisBottom: {
                  format: (value) => formatMonthLabel(value),
                },
                colors: ["#74c0fc", "#1864ab"],
                curve: "linear",
                enablePoints: true,
                pointSize: 8,
                pointBorderWidth: 2,
                pointColor: { theme: "background" },
                pointBorderColor: { from: "serieColor" },
                enableArea: false,
                enableGridY: true,
                useMesh: true,
                margin: { top: 50, right: 40, bottom: 100, left: 60 },
                legends: [
                  {
                    anchor: "bottom",
                    direction: "row",
                    justify: false,
                    translateX: 0,
                    translateY: 72,
                    itemsSpacing: 24,
                    itemDirection: "left-to-right",
                    itemWidth: 120,
                    itemHeight: 20,
                    itemOpacity: 0.85,
                    symbolSize: 10,
                    symbolShape: "circle",
                  },
                ],
                tooltip: ({ point }) => (
                  <div
                    style={{
                      background: theme.colorScheme === "dark" ? "#25262b" : "#fff",
                      color: theme.colorScheme === "dark" ? "#fff" : "#000",
                      padding: "8px 12px",
                      border: `1px solid ${
                        theme.colorScheme === "dark" ? "#373a40" : "#dee2e6"
                      }`,
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                  >
                    <strong>{formatMonthLabel(point.data.x)}</strong>
                    <div>
                      {point.serieId}: {formatCompactCurrency(point.data.y)}
                    </div>
                  </div>
                ),
              }}
            />

            <Group position="right">
              <ChartMetricToggle
                value={donutMetric}
                onChange={setDonutMetric}
                options={metricToggleOptions}
              />
            </Group>

            <Grid gutter="md">
              <Grid.Col span={12} md={6}>
                <PieChart
                  title={t(
                    "dashboard.salesPerformance.policyTypeDistribution",
                    "GI Policy Type Distribution"
                  )}
                  staticData={policyTypePieData}
                  height={380}
                  chartProps={{
                    colors: blueShades,
                    valueFormat: (value) =>
                      donutMetric === "premium"
                        ? formatCompactCurrency(value)
                        : numFormatter.format(Number(value)),
                    arcLabelsTextColor: "#ffffff",
                    tooltip: ({ datum }) => (
                      <div
                        style={{
                          background:
                            theme.colorScheme === "dark" ? "#25262b" : "#fff",
                          padding: "8px 12px",
                          borderRadius: 4,
                          fontSize: 12,
                        }}
                      >
                        <strong>{datum.label}</strong>
                        <div>
                          {donutMetric === "premium"
                            ? formatCompactCurrency(datum.value)
                            : numFormatter.format(datum.value)}
                        </div>
                      </div>
                    ),
                  }}
                />
              </Grid.Col>
              <Grid.Col span={12} md={6}>
                <PieChart
                  title={t(
                    "dashboard.salesPerformance.bizLinesDistribution",
                    "Biz Lines Distribution"
                  )}
                  staticData={bizLinesPieData}
                  height={380}
                  chartProps={{
                    colors: { scheme: "category10" },
                    valueFormat: (value) =>
                      donutMetric === "premium"
                        ? formatCompactCurrency(value)
                        : numFormatter.format(Number(value)),
                    arcLabelsTextColor: "#ffffff",
                    tooltip: ({ datum }) => (
                      <div
                        style={{
                          background:
                            theme.colorScheme === "dark" ? "#25262b" : "#fff",
                          padding: "8px 12px",
                          borderRadius: 4,
                          fontSize: 12,
                        }}
                      >
                        <strong>{datum.label}</strong>
                        <div>
                          {donutMetric === "premium"
                            ? formatCompactCurrency(datum.value)
                            : numFormatter.format(datum.value)}
                        </div>
                      </div>
                    ),
                  }}
                />
              </Grid.Col>
            </Grid>

            <BarChart
              title={t(
                "dashboard.salesPerformance.topSalesChannels",
                "Top 5 Sales Channels"
              )}
              height={380}
              data={topSalesChannels}
              useSampleData={false}
              chartProps={{
                keys: ["grossPremium"],
                indexBy: "name",
                layout: "vertical",
                colors: blueShades,
                enableLabel: true,
                labelSkipWidth: 12,
                labelSkipHeight: 12,
                valueFormat: (value) => formatCompactCurrency(value),
                margin: { top: 20, right: 40, bottom: 80, left: 60 },
                axisLeft: {
                  format: (value) => formatCompactCurrency(value),
                },
                axisBottom: {
                  tickRotation: 0,
                  format: (value) =>
                    value?.length > 14 ? `${value.substring(0, 14)}...` : value,
                },
                legends: [
                  {
                    anchor: "bottom",
                    direction: "row",
                    justify: false,
                    translateX: 0,
                    translateY: 56,
                    itemsSpacing: 16,
                    itemDirection: "left-to-right",
                    itemWidth: 100,
                    itemHeight: 20,
                    symbolSize: 10,
                  },
                ],
              }}
            />

            <Box sx={{ overflow: "auto" }}>
              <Group position="right" mb={4}>
                <ChartMetricToggle
                  value={insuranceClassMetric}
                  onChange={setInsuranceClassMetric}
                  options={metricToggleOptions}
                />
              </Group>
              <BarChart
                title={insuranceClassTitle}
                height={Math.max(380, insuranceClassData.length * 28)}
                data={insuranceClassData}
                useSampleData={false}
                chartProps={{
                  keys: ["value"],
                  layout: "horizontal",
                  indexBy: "id",
                  colors: blueShades,
                  margin: { top: 20, right: 60, bottom: 60, left: 180 },
                  axisLeft: {
                    format: truncateLabel,
                  },
                  axisBottom: {
                    format: (value) =>
                      insuranceClassMetric === "premium"
                        ? formatCompactCurrency(value)
                        : numFormatter.format(Number(value)),
                  },
                  valueFormat: (value) =>
                    insuranceClassMetric === "premium"
                      ? formatCompactCurrency(value)
                      : numFormatter.format(Number(value)),
                  legends: [
                    {
                      dataFrom: "keys",
                      anchor: "bottom",
                      direction: "row",
                      justify: false,
                      translateX: 0,
                      translateY: 48,
                      itemsSpacing: 16,
                      itemDirection: "left-to-right",
                      itemWidth: 100,
                      itemHeight: 20,
                      symbolSize: 10,
                    },
                  ],
                }}
              />
            </Box>

            <Box sx={{ overflow: "auto" }}>
              <Group position="right" mb={4}>
                <ChartMetricToggle
                  value={insurerMetric}
                  onChange={setInsurerMetric}
                  options={metricToggleOptions}
                />
              </Group>
              <BarChart
                title={insurerTitle}
                height={Math.max(380, insurerData.length * 28)}
                data={insurerData}
                useSampleData={false}
                chartProps={{
                  keys: ["value"],
                  layout: "horizontal",
                  indexBy: "id",
                  colors: blueShades,
                  margin: { top: 20, right: 80, bottom: 60, left: 180 },
                  axisLeft: {
                    format: truncateLabel,
                  },
                  axisBottom: {
                    format: (value) =>
                      insurerMetric === "premium"
                        ? formatCompactCurrency(value)
                        : numFormatter.format(Number(value)),
                  },
                  valueFormat: (value) =>
                    insurerMetric === "premium"
                      ? formatCompactCurrency(value)
                      : numFormatter.format(Number(value)),
                  legends: [
                    {
                      dataFrom: "keys",
                      anchor: "bottom",
                      direction: "row",
                      justify: false,
                      translateX: 0,
                      translateY: 48,
                      itemsSpacing: 16,
                      itemDirection: "left-to-right",
                      itemWidth: 100,
                      itemHeight: 20,
                      symbolSize: 10,
                    },
                  ],
                }}
              />
            </Box>
          </Stack>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default DashboardSalesPerformanceTab;
